import { Link } from 'react-router';
import { useLocale, useT } from '../i18n/locale-context';
import { localePath } from '../i18n/locale-path';

interface NicheHintProps {
  /** What the user actually typed — always shown, never hidden behind the niche. */
  q: string;
  niches: Array<{ slug: string; name: string }>;
  /** Slug of the niche whose lots were appended to the list below, if any. */
  applied?: string;
}

/**
 * "Your words found little; they name one of our categories."
 *
 * ## Why this exists
 *
 * Replaying 3 985 real searches from `events.search_performed` against the live
 * catalogue (2026-08-13) showed the commonest zero-result queries on the site
 * are its OWN category names — «наружная реклама» (24 searches, 8 people),
 * «клининг» (14/7), «тонер», «полиграфия», «электрика». The lots are there:
 * «клининг» has zero matches in any tender text, while `уборк:*` has 6 and
 * `tozalash:*` 19. The buyer types the industry word; the tender is written in
 * the plain one.
 *
 * Before this, that user got {@link NoResultsAlert} — a card offering to notify
 * them when such a tender appears. That was not merely unhelpful, it was
 * **false**: it says the catalogue has nothing like this today, and the
 * catalogue had 25.
 *
 * ## Two shapes, and why the ambiguous one does not substitute
 *
 * The niche resolver matches loosely (substring either way) because it powers a
 * type-ahead where the user sees and picks. Substituting results is not a
 * type-ahead, so:
 *
 * - **one niche matched** → its lots are appended below the literal ones and
 *   this banner says so. Measured: «клининг», «наружная реклама», «тонер»,
 *   «полиграфия», «электрика» each resolve to exactly one.
 * - **several matched** → nothing is substituted; the niches are offered as
 *   links. `it` (19 searches, the second-commonest zero-result query) resolves
 *   to FIVE niches on a two-character substring collision. Picking one for the
 *   user there would be a guess dressed as an answer.
 *
 * The query is always quoted back. A page that silently swaps what you searched
 * for is the same class of claim as a truncated `total` rendered as a real one.
 */
export function NicheHint({ q, niches, applied }: NicheHintProps) {
  const t = useT();
  const { locale } = useLocale();
  if (niches.length === 0) return null;
  const appliedNiche = applied ? niches.find((n) => n.slug === applied) : undefined;
  const href = (slug: string) => localePath(`/catalog?niche=${encodeURIComponent(slug)}`, locale);

  return (
    <div className="mb-4 rounded-lg border border-edge bg-bone/60 px-4 py-3 text-sm text-ink">
      {niches.length === 1 ? (
        <p>
          {appliedNiche
            ? t('niche.hint.applied', { q, niche: niches[0].name })
            : t('niche.hint.suggest', { q, niche: niches[0].name })}{' '}
          <Link to={href(niches[0].slug)} className="font-medium text-brand hover:underline">
            {t('niche.hint.open')}
          </Link>
        </p>
      ) : (
        <>
          <p className="mb-2">{t('niche.hint.ambiguous')}</p>
          <ul className="flex flex-wrap gap-2">
            {niches.map((n) => (
              <li key={n.slug}>
                <Link
                  to={href(n.slug)}
                  className="inline-block rounded-full border border-edge px-3 py-1 text-xs font-medium text-brand hover:bg-bone"
                >
                  {n.name}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
